/**
 * Financial Reports Page
 * 
 * Comprehensive financial reports and analytics.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FileText,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Download,
  Loader2,
  Calendar,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Coffee,
  Wallet,
  ChevronDown,
  Lock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LabelList,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { FeatureGuard, usePlanFeatures } from '@/hooks/use-plan-features';
import { FeatureLockButton, FeatureLockedCard } from '@/components/subscription/FeatureBlockedPage';
import { FEATURES_REPORTS_TABS, FEATURE_UNAVAILABLE_MESSAGE } from '@/config/features';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { KPICard, PeriodNavigator, CurrencyDisplay } from '@/components/financial';
import {
  useCurrentBudgetPeriod,
  useBudgetPeriodNavigation,
  useNavigatePeriod,
  useDailyExpenses,
  useMonthlyExpenses,
  useRevenues,
} from '@/hooks/use-financial';
import { reportsService } from '@/services/financial.service';
import { getInclusiveDaysRemaining, parseDateStringToLocalDate } from '@/lib/date';
import { buildTimelineChartRows, getPoAxisProps } from '@/lib/report-timeline';
import { selectExpenseInRevenueTotals } from '@/lib/expense-in-revenue';
import { renderRevenueTimelineLegendLabel } from './revenue-timeline-legend';
import { translateExpenseType, translateRevenueType, translateCategory } from '@/lib/translations';
import type { CashFlowMode } from '@/types/financial';
import { toast } from '@/hooks/use-toast';

const COLORS = [
  'hsl(174, 62%, 38%)',
  'hsl(38, 92%, 50%)',
  'hsl(152, 69%, 40%)',
  'hsl(199, 89%, 48%)',
  'hsl(280, 65%, 60%)',
  'hsl(0, 72%, 51%)',
  'hsl(220, 14%, 50%)',
];

const PAYMENT_METHOD_COLORS: Record<string, string> = {
  'boleto bancário': 'hsl(174, 62%, 38%)',
  'cartão de crédito': 'hsl(280, 65%, 60%)',
  'cartão de débito': 'hsl(38, 92%, 50%)',
  'pix': 'hsl(199, 89%, 48%)',
};

// Fixed stacking order (bottom to top) and colors for the revenue type
// timeline chart, kept stable regardless of each type's realized total.
const REVENUE_TIMELINE_TYPE_ORDER = ['continuous', 'installment', 'single', 'fractional'];

// As quatro fatias da pilha precisam ser distinguíveis mesmo quando o filtro de
// Tipos esconde uma série do meio e faz duas fatias antes separadas encostarem —
// por isso a paleta é validada em TODOS os pares, não só nos adjacentes.
//
// A combinação anterior reprovava: `single` (verde 152°) e `continuous` (teal
// 174°) ficavam a 22° de matiz e 2pp de luminosidade, ΔE 8.1 em visão normal
// (piso 15). O verde sai e entra um roxo, e o azul aprofunda para se afastar
// do teal da marca. O roxo é vizinho de `--chart-5` (280 65% 60%), não o token
// em si: 280 85% 58% foi o passo que fechou a separação — não troque por
// `hsl(var(--chart-5))` sem revalidar.
//
// O teal de `continuous` é propositalmente mais claro e dessaturado que o tom
// da marca: é a fatia dominante da pilha e um tom cheio pesava demais no card.
// 44% de luminosidade é o teto — acima disso a cor sai da faixa do tema escuro,
// e abaixo de ~48% de saturação o croma cai do piso 0.10 e o teal lê como cinza.
//
// `installment` mantém o âmbar claro original por decisão de produto. Ele fica
// fora da faixa de luminosidade (L 0.77) e rende só 2.13:1 sobre o card branco;
// qualquer âmbar aprovado cai para ~43% de luminosidade e vira mostarda, que
// foi recusado. Não afeta a distinção entre as séries — o âmbar não aparece em
// nenhum dos piores pares abaixo.
//
// Estas cores são de MARCA, não de texto. A legenda e o tooltip escrevem em
// `text-foreground` e carregam a identidade num marcador colorido ao lado (mesmo
// padrão da legenda de formas de pagamento acima). Pintar o rótulo com a cor da
// série derrubava o texto para 2.13:1 no tema claro e 3.05:1 no escuro.
//
// Medido com o validador de paleta (OKLab ΔE ×100, protan/deutan Machado 2009),
// contra as superfícies reais (card branco no claro, 220 20% 14% no escuro):
//   pior par sob daltonismo  ΔE 10.4  (alvo >= 8)
//   pior par em visão normal ΔE 17.9  (piso >= 15)
//   contraste de marca no card branco: teal 2.89:1 e âmbar 2.13:1, abaixo de
//   3:1 — a leitura fica por conta dos rótulos e valores, agora legíveis.
//
// Margem apertada: no tema escuro `fractional` rende 3.03:1 sobre o card, quase
// no piso de 3:1. Se `--card` do dark mudar de tom, revalide antes de subir.
const REVENUE_TIMELINE_TYPE_COLORS: Record<string, string> = {
  continuous: 'hsl(174, 50%, 44%)',
  installment: 'hsl(38, 92%, 50%)',
  single: 'hsl(280, 85%, 58%)',
  fractional: 'hsl(206, 70%, 40%)',
};

/**
 * Primeiro rótulo cujo valor é positivo; `padrao` quando nenhum for.
 *
 * Substitui a cadeia `a > 0 ? 'x' : b > 0 ? 'y' : ...` (typescript:S3358). O
 * resultado é idêntico: `find` devolve o primeiro par que satisfaz a condição,
 * na mesma ordem em que os ternários eram avaliados.
 */
const primeiroRotuloPositivo = (pares: Array<[number, string]>, padrao = ''): string =>
  pares.find(([valor]) => valor > 0)?.[1] ?? padrao;

// Título e descrição do card variam com o modo de visualização. Mapa em vez de
// cadeia de ternários (typescript:S3358): a chave ausente cai no padrão, que é
// exatamente o que o `else` final fazia.
const TITULO_RECEITA_X_DESPESA: Record<string, string> = {
  expense_breakdown: 'Despesa por Tipo',
  revenue_breakdown: 'Receita por Tipo',
};
const TITULO_RECEITA_X_DESPESA_PADRAO = 'Comparativo Receita x Despesa';

const DESCRICAO_RECEITA_X_DESPESA: Record<string, string> = {
  expense_breakdown: 'Distribuição das despesas realizadas por tipo',
  revenue_breakdown: 'Distribuição das receitas realizadas por tipo',
};
const DESCRICAO_RECEITA_X_DESPESA_PADRAO = 'Clique nas barras para detalhar por tipo';

const getPaymentMethodColor = (name: string, fallbackIndex: number): string => {
  const key = String(name || '').trim().toLowerCase();
  return PAYMENT_METHOD_COLORS[key] || COLORS[fallbackIndex % COLORS.length];
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: value > 100000 ? 'compact' : 'standard',
  }).format(value);
};

const formatCashFlowDate = (value?: string | null) => {
  if (!value) return '-';

  const parsed = parseDateStringToLocalDate(value);
  if (!parsed) return '-';

  return format(parsed, 'dd/MM/yyyy', { locale: ptBR });
};

const formatPercentage = (value: number) => {
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)}%`;
};

const renderStackedColumnTotalLabel = (
  props: Pick<React.SVGProps<SVGTextElement>, 'x' | 'y' | 'width' | 'height'> & { index?: number },
  data: Array<{ total_amount: number; top_label_key: string }>,
  expectedDataKey: string
) => {
  const index = Number(props?.index ?? -1);
  const row = Number.isNaN(index) || index < 0 ? undefined : data[index];
  const totalAmount = Number(row?.total_amount || 0);

  if (row?.top_label_key !== expectedDataKey || totalAmount <= 0) {
    return null;
  }

  return (
    <text
      x={Number(props?.x || 0) + Number(props?.width || 0) / 2}
      y={Number(props?.y || 0) - 8}
      textAnchor="middle"
      fill="hsl(var(--muted-foreground))"
      fontSize={14}
      fontWeight={600}
    >
      {formatCurrency(totalAmount)}
    </text>
  );
};

const renderStackedRowTotalLabel = (
  props: Pick<React.SVGProps<SVGTextElement>, 'x' | 'y' | 'width' | 'height'> & { index?: number },
  data: Array<{ total_amount: number; top_label_key: string }>,
  expectedDataKey: string
) => {
  const index = Number(props?.index ?? -1);
  const row = Number.isNaN(index) || index < 0 ? undefined : data[index];
  const totalAmount = Number(row?.total_amount || 0);

  if (row?.top_label_key !== expectedDataKey || totalAmount <= 0) {
    return null;
  }

  return (
    <text
      x={Number(props?.x || 0) + Number(props?.width || 0) + 8}
      y={Number(props?.y || 0) + Number(props?.height || 0) / 2}
      textAnchor="start"
      dominantBaseline="central"
      fill="hsl(var(--muted-foreground))"
      fontSize={14}
      fontWeight={600}
    >
      {formatCurrency(totalAmount)}
    </text>
  );
};

const REPORT_TABS = [
  { value: 'revenues', feature: FEATURES_REPORTS_TABS.ABA_RECEITAS, label: 'Receitas', icon: ArrowUpRight },
  { value: 'expenses', feature: FEATURES_REPORTS_TABS.ABA_DESPESAS, label: 'Despesas', icon: ArrowDownRight },
  { value: 'trends', feature: FEATURES_REPORTS_TABS.ABA_TENDENCIAS, label: 'Tendências', icon: TrendingUp },
  { value: 'summary', feature: FEATURES_REPORTS_TABS.ABA_RESUMO, label: 'Resumo', icon: BarChart3 },
  { value: 'cashflow', feature: FEATURES_REPORTS_TABS.ABA_FLUXO_DE_CAIXA, label: 'Fluxo de Caixa', icon: Wallet },
] as const;

export function ReportsPage() {
  const { hasFeature } = usePlanFeatures();
  const isCompactScreen = useIsMobile();
  const [activeTab, setActiveTab] = useState('revenues');

  // Abas de Relatórios visíveis conforme as features habilitadas no plano.
  const visibleReportTabs: string[] = REPORT_TABS.filter((t) => hasFeature(t.feature)).map((t) => t.value);
  const visibleTabsKey = visibleReportTabs.join(',');
  useEffect(() => {
    if (visibleReportTabs.length > 0 && !visibleReportTabs.includes(activeTab)) {
      setActiveTab(visibleReportTabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabsKey, activeTab]);

  // Verifica se uma aba está bloqueada pelo plano (feature não habilitada).
  const isTabLocked = (value: string): boolean => {
    const tab = REPORT_TABS.find((t) => t.value === value);
    return tab ? !hasFeature(tab.feature) : false;
  };
  // Barra a entrada em abas bloqueadas: mantém o trigger hoverável (para o
  // tooltip aparecer) e apenas informa que a funcionalidade não está no plano.
  const handleTabChange = (value: string): void => {
    if (isTabLocked(value)) {
      toast({
        variant: 'destructive',
        title: 'Recurso bloqueado',
        description: FEATURE_UNAVAILABLE_MESSAGE,
      });
      return;
    }
    setActiveTab(value);
  };
  const [expenseSectionTab, setExpenseSectionTab] = useState('expense');
  const [cashFlowMode, setCashFlowMode] = useState<CashFlowMode>('planned');
  const [periodsBack, setPeriodsBack] = useState(6);
  const [plannedVsRealizedMode, setPlannedVsRealizedMode] = useState<'revenue' | 'expense'>('revenue');
  const [revenueVsExpenseView, setRevenueVsExpenseView] = useState<
    'summary' | 'expense_breakdown' | 'revenue_breakdown'
  >('summary');
  const [expenseTimelinePeriodsBack, setExpenseTimelinePeriodsBack] = useState(6);
  const [selectedExpenseTimelineCategories, setSelectedExpenseTimelineCategories] = useState<string[]>([]);
  const [revenueTimelinePeriodsBack, setRevenueTimelinePeriodsBack] = useState(6);
  const [selectedRevenueTimelineTypes, setSelectedRevenueTimelineTypes] = useState<string[]>([]);
  const [futureExpenseMonthsAhead, setFutureExpenseMonthsAhead] = useState<6 | 12 | 18 | 24>(12);
  const [futureExpenseView, setFutureExpenseView] = useState<'accumulated' | 'unit'>('accumulated');
  const [futureExpenseSelectedId, setFutureExpenseSelectedId] = useState<string | null>(null);
  const [agentAnalysisMode, setAgentAnalysisMode] = useState<'revenue' | 'expense'>('revenue');
  // Abre em "Planejado": é o que existe desde o início do período. "Realizado"
  // pode ser legitimamente zero no começo do PO, e abrir num gráfico vazio não
  // informa nada — o usuário alterna quando quiser ver a execução.
  const [expenseInRevenueMode, setExpenseInRevenueMode] = useState<'realized' | 'planned'>('planned');
  const [revenueByTypeMode, setRevenueByTypeMode] = useState<'planned' | 'realized'>('planned');

  const { data: currentPeriod, isLoading: isPeriodLoading } = useCurrentBudgetPeriod();
  const { data: navigation } = useBudgetPeriodNavigation();
  const { mutate: navigatePeriod, isPending: isNavigating } = useNavigatePeriod();

  // Dados operacionais (antes exibidos como "2ª linha de cards" em algumas páginas)
  const { data: revenues } = useRevenues(currentPeriod?.id);
  const { data: monthlyExpenses } = useMonthlyExpenses(currentPeriod?.id);
  const { data: dailyExpenses } = useDailyExpenses(currentPeriod?.id);
  
  const { data: summaryReport, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['reports', 'summary', currentPeriod?.id],
    queryFn: () => reportsService.getPeriodSummary(currentPeriod?.id),
    enabled: !!currentPeriod?.id,
  });
  
  const { data: trendReport, isLoading: isTrendLoading } = useQuery({
    queryKey: ['reports', 'trend', periodsBack],
    queryFn: () => reportsService.getTrendAnalysis(periodsBack),
  });
  
  const { data: expenseReport, isLoading: isExpenseLoading } = useQuery({
    queryKey: ['reports', 'expense', currentPeriod?.id],
    queryFn: () => reportsService.getExpenseAnalysis(currentPeriod?.id),
    enabled: !!currentPeriod?.id,
  });

  const {
    data: expenseCategoryTimelineReport,
    isLoading: isExpenseCategoryTimelineLoading,
    isError: isExpenseCategoryTimelineError,
  } = useQuery({
    queryKey: ['reports', 'expense-category-timeline', currentPeriod?.id, expenseTimelinePeriodsBack],
    queryFn: () => reportsService.getExpenseCategoryTimeline(expenseTimelinePeriodsBack, currentPeriod?.id),
    enabled: !!currentPeriod?.id && activeTab === 'expenses' && expenseSectionTab === 'expense',
  });

  const {
    data: revenueTypeTimelineReport,
    isLoading: isRevenueTypeTimelineLoading,
    isError: isRevenueTypeTimelineError,
  } = useQuery({
    queryKey: ['reports', 'revenue-type-timeline', currentPeriod?.id, revenueTimelinePeriodsBack],
    queryFn: () => reportsService.getRevenueTypeTimeline(revenueTimelinePeriodsBack, currentPeriod?.id),
    enabled: !!currentPeriod?.id && activeTab === 'revenues',
  });
  
  const { data: revenueReport, isLoading: isRevenueLoading } = useQuery({
    queryKey: ['reports', 'revenue', currentPeriod?.id],
    queryFn: () => reportsService.getRevenueAnalysis(currentPeriod?.id),
    enabled: !!currentPeriod?.id,
  });

  const { data: cashFlowReport, isLoading: isCashFlowLoading } = useQuery({
    queryKey: ['reports', 'cash-flow', currentPeriod?.id, cashFlowMode],
    queryFn: () => reportsService.getCashFlow(cashFlowMode, currentPeriod?.id),
    enabled: !!currentPeriod?.id && activeTab === 'cashflow',
  });

  const { data: futureExpenseSummaryReport, isLoading: isFutureExpenseSummaryLoading } = useQuery({
    queryKey: ['reports', 'future-expenses-summary', currentPeriod?.id],
    queryFn: () => reportsService.getFutureExpenseSummary(currentPeriod?.id),
    enabled: !!currentPeriod?.id && activeTab === 'expenses' && expenseSectionTab === 'future-expenses',
  });

  const {
    data: futureExpenseProjectionReport,
    isLoading: isFutureExpenseProjectionLoading,
    isError: isFutureExpenseProjectionError,
  } = useQuery({
    queryKey: ['reports', 'future-expense-projection', currentPeriod?.id, futureExpenseMonthsAhead],
    queryFn: () => reportsService.getFutureExpenseProjection(futureExpenseMonthsAhead, currentPeriod?.id),
    enabled: !!currentPeriod?.id && activeTab === 'expenses' && expenseSectionTab === 'future-expenses',
  });

  useEffect(() => {
    if (!expenseCategoryTimelineReport) {
      return;
    }

    setSelectedExpenseTimelineCategories(expenseCategoryTimelineReport.default_selected_categories);
  }, [expenseCategoryTimelineReport]);

  useEffect(() => {
    if (!revenueTypeTimelineReport) {
      return;
    }

    setSelectedRevenueTimelineTypes(revenueTypeTimelineReport.default_selected_types);
  }, [revenueTypeTimelineReport]);

  useEffect(() => {
    if (!futureExpenseProjectionReport || (futureExpenseProjectionReport.expenses ?? []).length === 0) {
      setFutureExpenseSelectedId(null);
      return;
    }
    const exists = (futureExpenseProjectionReport.expenses ?? []).some((e) => e.id === futureExpenseSelectedId);
    if (!exists) {
      setFutureExpenseSelectedId(futureExpenseProjectionReport.expenses[0].id);
    }
  }, [futureExpenseProjectionReport, futureExpenseSelectedId]);
  
  const isLoading = isPeriodLoading || isSummaryLoading;
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  const revenuePendingCount =
    revenues?.filter((r) => String(r.status || '').toLowerCase() === 'pending').length || 0;
  const revenueRealizedCount =
    revenues?.filter((r) => String(r.status || '').toLowerCase() === 'realized').length || 0;

  const monthlyPendingCount =
    monthlyExpenses?.filter((e) => String(e.status || '').toLowerCase() === 'pending').length || 0;
  const monthlyOverdueCount = monthlyExpenses?.filter((e) => Boolean(e.is_overdue)).length || 0;

  const dailyPlannedTotal =
    dailyExpenses?.reduce((sum: number, e) => sum + Number(e.planned_total || 0), 0) || 0;
  const dailyRealizedTotal =
    dailyExpenses?.reduce((sum: number, e) => sum + Number(e.realized_total || 0), 0) || 0;
  const dailyRemainingBudget = dailyPlannedTotal - dailyRealizedTotal;

  const daysRemaining = getInclusiveDaysRemaining(currentPeriod?.end_date);
  const dailyCapacity = daysRemaining > 0 ? dailyRemainingBudget / daysRemaining : 0;

  // O backend já entrega uma linha por mês, em ordem de data, com o rótulo do PO.
  const trendChartData = trendReport?.periods || [];

  const periodLabel = (() => {
    const start = currentPeriod?.start_date ? parseDateStringToLocalDate(currentPeriod.start_date) : null;
    const end = currentPeriod?.end_date ? parseDateStringToLocalDate(currentPeriod.end_date) : null;
    return start && end
      ? `Período Orçamentário ${format(start, 'dd/MM', { locale: ptBR })} - ${format(end, 'dd/MM', { locale: ptBR })}`
      : undefined;
  })();

  const monthlyExpenseByType = (expenseReport?.by_type || []).reduce(
    (acc: Record<string, { planned: number; realized: number }>, item) => {
      const key = String(item?.type || 'other').toLowerCase();
      acc[key] = {
        planned: Number(item?.planned || 0),
        realized: Number(item?.realized || 0),
      };
      return acc;
    },
    {}
  );

  const revenuePlanned = Number(summaryReport?.summary?.total_planned_revenue || 0);
  const revenueRealized = Number(summaryReport?.summary?.total_realized_revenue || 0);
  const futurePlanned = Number(currentPeriod?.planned_future_expense || 0);
  const futureRealized = Number(currentPeriod?.realized_future_expense || 0);
  const dailyPlanned = Number(currentPeriod?.planned_daily_expense || 0);
  const dailyRealized = Number(currentPeriod?.realized_daily_expense || 0);
  const monthlyContinuousPlanned = Number(monthlyExpenseByType.continuous?.planned || 0);
  const monthlyContinuousRealized = Number(monthlyExpenseByType.continuous?.realized || 0);
  const monthlySinglePlanned = Number(monthlyExpenseByType.single?.planned || 0);
  const monthlySingleRealized = Number(monthlyExpenseByType.single?.realized || 0);
  const monthlyInstallmentPlanned = Number(monthlyExpenseByType.installment?.planned || 0);
  const monthlyInstallmentRealized = Number(monthlyExpenseByType.installment?.realized || 0);
  const monthlyRealizedTotal =
    monthlyContinuousRealized + monthlySingleRealized + monthlyInstallmentRealized;
  const monthlyTopLabelKey = primeiroRotuloPositivo([
    [monthlyInstallmentRealized, 'monthly_installment'],
    [monthlySingleRealized, 'monthly_single'],
    [monthlyContinuousRealized, 'monthly_continuous'],
  ]);

  // Gráfico "Despesas Dentro da Receita" pode alternar entre Realizado e Planejado.
  const isExpenseInRevenuePlanned = expenseInRevenueMode === 'planned';
  const {
    revenue: eirRevenue,
    future: eirFuture,
    daily: eirDaily,
    monthlyContinuous: eirMonthlyContinuous,
    monthlySingle: eirMonthlySingle,
    monthlyInstallment: eirMonthlyInstallment,
  } = selectExpenseInRevenueTotals(
    expenseInRevenueMode,
    {
      revenue: revenuePlanned,
      future: futurePlanned,
      daily: dailyPlanned,
      monthlyContinuous: monthlyContinuousPlanned,
      monthlySingle: monthlySinglePlanned,
      monthlyInstallment: monthlyInstallmentPlanned,
    },
    {
      revenue: revenueRealized,
      future: futureRealized,
      daily: dailyRealized,
      monthlyContinuous: monthlyContinuousRealized,
      monthlySingle: monthlySingleRealized,
      monthlyInstallment: monthlyInstallmentRealized,
    }
  );
  const eirMonthlyTotal = eirMonthlyContinuous + eirMonthlySingle + eirMonthlyInstallment;
  const eirMonthlyTopLabelKey = primeiroRotuloPositivo([
    [eirMonthlyInstallment, 'monthly_installment'],
    [eirMonthlySingle, 'monthly_single'],
    [eirMonthlyContinuous, 'monthly_continuous'],
  ]);

  const revenueBase = eirRevenue;
  const expenseInRevenueChartData = [
    {
      label: 'Receita',
      receita: revenueBase,
      receita_amount: revenueBase,
      future: 0,
      future_amount: 0,
      daily: 0,
      daily_amount: 0,
      monthly_continuous: 0,
      monthly_continuous_amount: 0,
      monthly_single: 0,
      monthly_single_amount: 0,
      monthly_installment: 0,
      monthly_installment_amount: 0,
      total_amount: revenueBase,
      top_label_key: 'receita',
    },
    {
      label: 'Despesa Futura',
      receita: 0,
      receita_amount: 0,
      future: eirFuture,
      future_amount: eirFuture,
      daily: 0,
      daily_amount: 0,
      monthly_continuous: 0,
      monthly_continuous_amount: 0,
      monthly_single: 0,
      monthly_single_amount: 0,
      monthly_installment: 0,
      monthly_installment_amount: 0,
      total_amount: eirFuture,
      top_label_key: 'future',
    },
    {
      label: 'Despesa Mensal',
      receita: 0,
      receita_amount: 0,
      future: 0,
      future_amount: 0,
      daily: 0,
      daily_amount: 0,
      monthly_continuous: eirMonthlyContinuous,
      monthly_continuous_amount: eirMonthlyContinuous,
      monthly_single: eirMonthlySingle,
      monthly_single_amount: eirMonthlySingle,
      monthly_installment: eirMonthlyInstallment,
      monthly_installment_amount: eirMonthlyInstallment,
      total_amount: eirMonthlyTotal,
      top_label_key: eirMonthlyTopLabelKey,
    },
    {
      label: 'Despesa Dia a Dia',
      receita: 0,
      receita_amount: 0,
      future: 0,
      future_amount: 0,
      daily: eirDaily,
      daily_amount: eirDaily,
      monthly_continuous: 0,
      monthly_continuous_amount: 0,
      monthly_single: 0,
      monthly_single_amount: 0,
      monthly_installment: 0,
      monthly_installment_amount: 0,
      total_amount: eirDaily,
      top_label_key: 'daily',
    },
  ];

  const expenseRealized = Number(summaryReport?.summary?.total_realized_expense || 0);
  const revenueVsExpenseSummaryData = [
    {
      label: 'Receita',
      realized: revenueRealized,
      color: 'hsl(152, 69%, 40%)',
    },
    {
      label: 'Despesa',
      realized: expenseRealized,
      color: 'hsl(0, 72%, 51%)',
    },
  ];
  const expenseBreakdownData = [
    {
      label: 'Futura',
      future: futureRealized,
      monthly_continuous: 0,
      monthly_single: 0,
      monthly_installment: 0,
      daily: 0,
      total_amount: futureRealized,
      top_label_key: 'future',
    },
    {
      label: 'Mensal',
      future: 0,
      monthly_continuous: monthlyContinuousRealized,
      monthly_single: monthlySingleRealized,
      monthly_installment: monthlyInstallmentRealized,
      daily: 0,
      total_amount: monthlyRealizedTotal,
      top_label_key: monthlyTopLabelKey,
    },
    {
      label: 'Dia a Dia',
      future: 0,
      monthly_continuous: 0,
      monthly_single: 0,
      monthly_installment: 0,
      daily: dailyRealized,
      total_amount: dailyRealized,
      top_label_key: 'daily',
    },
  ];
  const expenseBreakdownSeries = [
    { key: 'future', name: 'Futura', color: 'hsl(280, 65%, 60%)', value: futureRealized },
    { key: 'monthly_continuous', name: 'Mensal Contínua', color: 'hsl(174, 62%, 38%)', value: monthlyContinuousRealized },
    { key: 'monthly_single', name: 'Mensal Única', color: 'hsl(38, 92%, 50%)', value: monthlySingleRealized },
    { key: 'monthly_installment', name: 'Mensal Parcelada', color: 'hsl(166, 70%, 32%)', value: monthlyInstallmentRealized },
    { key: 'daily', name: 'Dia a Dia', color: 'hsl(199, 89%, 48%)', value: dailyRealized },
  ];
  const revenueByTypeMap = (summaryReport?.revenue_by_type || []).reduce(
    (acc: Record<string, number>, item) => {
      const key = String(item?.type || '').toLowerCase();
      acc[key] = Number(item?.realized || 0);
      return acc;
    },
    {}
  );
  const revenueBreakdownData = [
    {
      label: 'Contínua',
      realized: revenueByTypeMap.continuous || 0,
      color: 'hsl(152, 69%, 40%)',
    },
    {
      label: 'Única',
      realized: revenueByTypeMap.single || 0,
      color: 'hsl(174, 62%, 38%)',
    },
    {
      label: 'Parcelada',
      realized: revenueByTypeMap.installment || 0,
      color: 'hsl(199, 89%, 48%)',
    },
    {
      label: 'Fracionada',
      realized: revenueByTypeMap.fractional || 0,
      color: 'hsl(38, 92%, 50%)',
    },
  ];
  const revenueVsExpenseChartData =
    revenueVsExpenseView === 'revenue_breakdown'
      ? revenueBreakdownData
      : revenueVsExpenseSummaryData;

  const paymentMethodReportRows = (expenseReport?.by_payment_method || []).filter(
    (item) => Number(item?.realized || 0) > 0
  );
  const paymentMethodChartData = paymentMethodReportRows.map((item) => ({
    ...item,
    name: item.method,
    value: Number(item?.realized || 0),
    percentage: Number(item?.percentage || 0),
  }));
  const paymentMethodBarChartData = [...paymentMethodChartData].sort(
    (a, b) => Number(b.value || 0) - Number(a.value || 0)
  );
  const paymentMethodRealizedTotal = paymentMethodReportRows.reduce(
    (sum: number, item) => sum + Number(item?.realized || 0),
    0
  );

  const agentAnalysisSourceRows =
    agentAnalysisMode === 'revenue'
      ? (revenueReport?.by_agent || [])
      : (expenseReport?.by_payer_agent || []);
  const agentAnalysisChartData = agentAnalysisSourceRows
    .filter((item) => Number(item?.realized || 0) > 0)
    .map((item) => ({
      ...item,
      name: item.agent_name || 'Sem agente',
      value: Number(item?.realized || 0),
      percentage: Number(item?.percentage || 0),
    }))
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  const agentAnalysisLoading =
    agentAnalysisMode === 'revenue' ? isRevenueLoading : isExpenseLoading;
  const agentAnalysisEmptyMessage =
    agentAnalysisMode === 'revenue'
      ? 'Nenhuma receita realizada com agente informado neste período.'
      : 'Nenhuma despesa realizada com agente informado neste período.';

  const expenseTimelineAvailableCategories = expenseCategoryTimelineReport?.categories || [];
  const expenseTimelineChartData = buildTimelineChartRows(
    expenseCategoryTimelineReport?.periods || [],
    expenseTimelineAvailableCategories,
    (category) => category.category
  );

  const selectedExpenseTimelineSeries = expenseTimelineAvailableCategories.filter((category) =>
    selectedExpenseTimelineCategories.includes(category.category)
  );

  const toggleExpenseTimelineCategory = (
    categoryName: string,
    checked: boolean | 'indeterminate'
  ) => {
    setSelectedExpenseTimelineCategories((currentCategories) => {
      if (checked) {
        return currentCategories.includes(categoryName)
          ? currentCategories
          : [...currentCategories, categoryName];
      }

      return currentCategories.filter((item) => item !== categoryName);
    });
  };

  const showAllExpenseTimelineCategories = () => {
    setSelectedExpenseTimelineCategories(
      expenseTimelineAvailableCategories.map((item) => item.category)
    );
  };

  const getExpenseTimelineColor = (categoryName: string) => {
    const categoryIndex = expenseTimelineAvailableCategories.findIndex(
      (item) => item.category === categoryName
    );

    return COLORS[Math.max(categoryIndex, 0) % COLORS.length];
  };

  const revenueTimelineAvailableTypes = revenueTypeTimelineReport?.types || [];
  const revenueTimelineChartData = buildTimelineChartRows(
    revenueTypeTimelineReport?.periods || [],
    revenueTimelineAvailableTypes,
    (type) => type.type
  );

  const getRevenueTimelineOrderIndex = (typeName: string) => {
    const orderIndex = REVENUE_TIMELINE_TYPE_ORDER.indexOf(typeName);
    return orderIndex === -1 ? REVENUE_TIMELINE_TYPE_ORDER.length : orderIndex;
  };

  const selectedRevenueTimelineSeries = revenueTimelineAvailableTypes
    .filter((type) => selectedRevenueTimelineTypes.includes(type.type))
    .slice()
    .sort(
      (left, right) =>
        getRevenueTimelineOrderIndex(left.type) - getRevenueTimelineOrderIndex(right.type)
    );

  const toggleRevenueTimelineType = (
    typeName: string,
    checked: boolean | 'indeterminate'
  ) => {
    setSelectedRevenueTimelineTypes((currentTypes) => {
      if (checked) {
        return currentTypes.includes(typeName)
          ? currentTypes
          : [...currentTypes, typeName];
      }

      return currentTypes.filter((item) => item !== typeName);
    });
  };

  const showAllRevenueTimelineTypes = () => {
    setSelectedRevenueTimelineTypes(
      revenueTimelineAvailableTypes.map((item) => item.type)
    );
  };

  const getRevenueTimelineColor = (typeName: string) => {
    if (REVENUE_TIMELINE_TYPE_COLORS[typeName]) {
      return REVENUE_TIMELINE_TYPE_COLORS[typeName];
    }

    const typeIndex = revenueTimelineAvailableTypes.findIndex(
      (item) => item.type === typeName
    );

    return COLORS[Math.max(typeIndex, 0) % COLORS.length];
  };

  const isRevenueByTypePlanned = revenueByTypeMode === 'planned';
  const revenueByTypeChartData = (revenueReport?.by_type || [])
    .map((item) => ({
      ...item,
      name: translateRevenueType(item.type),
      value: isRevenueByTypePlanned
        ? Number(item.planned || 0)
        : Number(item.realized || 0),
    }))
    .filter((item) => Number(item.value || 0) > 0);

  const handleExportCashFlowPdf = async () => {
    if (!cashFlowReport) return;

    try {
      const { blob, filename } = await reportsService.downloadCashFlowPdf(cashFlowMode, currentPeriod?.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.warn('Falha ao baixar o PDF do relatorio:', error);
      toast({
        title: 'Nao foi possivel baixar o PDF',
        description: 'Tente novamente em alguns instantes.',
        variant: 'destructive',
      });
    }
  };
  
  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Relatórios Financeiros
          </h1>
          <p className="text-muted-foreground">
            Análise detalhada das suas finanças
          </p>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <PeriodNavigator
            currentPeriod={currentPeriod}
            periods={navigation?.periods || []}
            isLoading={isNavigating}
            onNavigate={navigatePeriod}
            variant="boxed"
          />
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid h-auto w-full grid-cols-2 gap-2 md:grid-cols-5">
          {REPORT_TABS.map((tab) => {
            const locked = !hasFeature(tab.feature);
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                title={locked ? FEATURE_UNAVAILABLE_MESSAGE : undefined}
                aria-disabled={locked}
                className={locked ? 'gap-2 opacity-60' : 'gap-2'}
              >
                {locked ? <Lock className="h-4 w-4 text-amber-500" /> : <Icon className="h-4 w-4" />}
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        
        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-6 mt-6">
          {summaryReport && (
            <>
              {/* Summary KPIs */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <KPICard
                  title="Total Receitas"
                  value={(summaryReport.summary?.total_realized_revenue ?? 0)}
                  format="currency"
                  icon={ArrowUpRight}
                  variant="success"
                  subtitle={`${(summaryReport.summary?.revenue_realization_rate ?? 0)}% realizado`}
                />
                <KPICard
                  title="Total Despesas"
                  value={(summaryReport.summary?.total_realized_expense ?? 0)}
                  format="currency"
                  icon={ArrowDownRight}
                  variant="danger"
                  subtitle={`${(summaryReport.summary?.expense_execution_rate ?? 0)}% executado`}
                />
                <KPICard
                  title="Saldo Realizado"
                  value={(summaryReport.summary?.realized_balance ?? 0)}
                  format="currency"
                  icon={DollarSign}
                  variant={(summaryReport.summary?.realized_balance ?? 0) >= 0 ? 'success' : 'danger'}
                />
                <KPICard
                  title="Total Lançamentos"
                  value={(summaryReport.summary?.total_revenue_count ?? 0) + (summaryReport.summary?.total_expense_count ?? 0)}
                  format="number"
                  icon={FileText}
                  subtitle={`${(summaryReport.summary?.total_revenue_count ?? 0)} receitas, ${(summaryReport.summary?.total_expense_count ?? 0)} despesas`}
                />
              </div>
              
              {/* Charts */}
              <div className="grid gap-6 lg:grid-cols-2">
                <FeatureGuard feature="relatorios-grafico-despesas-dentro-receita" fallback={<FeatureLockedCard title="Despesas Dentro da Receita" />}>
                <Card>
                  <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <CardTitle style={{ fontSize: '14px' }}>Despesas Dentro da Receita</CardTitle>
                      <CardDescription>
                        Distribuição da receita por despesas ({isExpenseInRevenuePlanned ? 'Planejado' : 'Realizado'})
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={expenseInRevenueMode === 'planned' ? 'default' : 'outline'}
                        onClick={() => setExpenseInRevenueMode('planned')}
                      >
                        Planejado
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={expenseInRevenueMode === 'realized' ? 'default' : 'outline'}
                        onClick={() => setExpenseInRevenueMode('realized')}
                      >
                        Realizado
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                      <div className="h-[330px]">
                        <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={expenseInRevenueChartData} margin={{ top: 34, right: 12, left: 24, bottom: 8 }}>
                          <XAxis dataKey="label" />
                          <YAxis
                            width={88}
                            tickFormatter={(value: number) => formatCurrency(value)}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload || payload.length === 0) return null;

                              const visibleItems = payload.filter((item) => Number(item?.value || 0) > 0);
                              if (visibleItems.length === 0) return null;

                              return (
                                <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                  <p className="mb-2 text-sm font-medium">{label}</p>
                                  <div className="space-y-1">
                                    {visibleItems.map((item) => {
                                      const amount = Number(item?.payload?.[`${item?.dataKey}_amount`] || 0);
                                      const percentage = revenueBase > 0 ? (amount / revenueBase) * 100 : 0;
                                      return (
                                        <p key={item.dataKey} className="text-sm" style={{ color: item?.color }}>
                                          {item.name}: {formatCurrency(amount)} ({percentage.toFixed(1)}%)
                                        </p>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <Legend />
                          <Bar dataKey="receita" name="Receita" stackId="expense-in-revenue" fill="hsl(152, 69%, 40%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'receita')} />
                          </Bar>
                          <Bar dataKey="future" name="Futuras" stackId="expense-in-revenue" fill="hsl(280, 65%, 60%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'future')} />
                          </Bar>
                          <Bar dataKey="daily" name="Dia a Dia" stackId="expense-in-revenue" fill="hsl(199, 89%, 48%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'daily')} />
                          </Bar>
                          <Bar dataKey="monthly_continuous" name={`Mensal ${translateExpenseType('continuous')}`} stackId="expense-in-revenue" fill="hsl(174, 62%, 38%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'monthly_continuous')} />
                          </Bar>
                          <Bar dataKey="monthly_single" name={`Mensal ${translateExpenseType('single')}`} stackId="expense-in-revenue" fill="hsl(38, 92%, 50%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'monthly_single')} />
                          </Bar>
                          <Bar dataKey="monthly_installment" name={`Mensal ${translateExpenseType('installment')}`} stackId="expense-in-revenue" fill="hsl(0, 72%, 51%)" maxBarSize={56}>
                            <LabelList content={(props) => renderStackedColumnTotalLabel(props, expenseInRevenueChartData, 'monthly_installment')} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                </FeatureGuard>

                <FeatureGuard feature="relatorios-grafico-comparativo-receita-despesa" fallback={<FeatureLockedCard title="Comparativo Receita x Despesa" />}>
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle style={{ fontSize: '14px' }}>
                          {TITULO_RECEITA_X_DESPESA[revenueVsExpenseView] ??
                            TITULO_RECEITA_X_DESPESA_PADRAO}
                        </CardTitle>
                        <CardDescription>
                          {DESCRICAO_RECEITA_X_DESPESA[revenueVsExpenseView] ??
                            DESCRICAO_RECEITA_X_DESPESA_PADRAO}
                        </CardDescription>
                      </div>
                      {revenueVsExpenseView !== 'summary' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setRevenueVsExpenseView('summary')}
                        >
                          Voltar
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[330px]">
                      <ResponsiveContainer width="100%" height="100%">
                        {revenueVsExpenseView === 'expense_breakdown' ? (
                          <BarChart
                            data={expenseBreakdownData}
                            layout="vertical"
                            margin={{ top: 8, right: 96, left: 16, bottom: 8 }}
                          >
                            <XAxis type="number" tickFormatter={(value: number) => formatCurrency(value)} />
                            <YAxis type="category" dataKey="label" width={96} />
                            <Tooltip
                              content={({ active, payload, label }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                const visible = payload.filter((p) => Number(p?.value || 0) > 0);
                                if (visible.length === 0) return null;
                                return (
                                  <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                    <p className="mb-2 text-sm font-medium">{label}</p>
                                    <div className="space-y-1">
                                      {visible.map((item) => (
                                        <p key={item.dataKey} className="text-sm" style={{ color: item?.color }}>
                                          {item.name}: {formatCurrency(Number(item?.value || 0))}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }}
                            />
                            <Legend
                              content={() => (
                                <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
                                  {expenseBreakdownSeries.map((s) => (
                                    <div key={s.key} className="flex items-center gap-2 text-xs">
                                      <span
                                        className="inline-block h-2.5 w-2.5 rounded-sm"
                                        style={{ background: s.color }}
                                      />
                                      <span>
                                        {s.name}: {formatCurrency(s.value)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                            {expenseBreakdownSeries.map((s) => (
                              <Bar
                                key={s.key}
                                dataKey={s.key}
                                name={s.name}
                                stackId="expense-breakdown"
                                fill={s.color}
                                maxBarSize={48}
                              >
                                <LabelList
                                  content={(props) =>
                                    renderStackedRowTotalLabel(props, expenseBreakdownData, s.key)
                                  }
                                />
                              </Bar>
                            ))}
                          </BarChart>
                        ) : (
                          <BarChart
                            data={revenueVsExpenseChartData}
                            layout="vertical"
                            margin={{ top: 8, right: 96, left: 16, bottom: 8 }}
                          >
                            <XAxis type="number" tickFormatter={(value: number) => formatCurrency(value)} />
                            <YAxis type="category" dataKey="label" width={96} />
                            <Tooltip
                              content={({ active, payload, label }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                return (
                                  <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                    <p className="mb-2 text-sm font-medium">{label}</p>
                                    <div className="space-y-1">
                                      {payload.map((item) => (
                                        <p key={item.dataKey} className="text-sm" style={{ color: item?.color }}>
                                          {formatCurrency(Number(item?.value || 0))}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }}
                            />
                            <Bar
                              dataKey="realized"
                              name="Realizado"
                              radius={[0, 6, 6, 0]}
                              maxBarSize={48}
                              onClick={(data) => {
                                if (revenueVsExpenseView !== 'summary') return;
                                if (data?.label === 'Despesa') {
                                  setRevenueVsExpenseView('expense_breakdown');
                                } else if (data?.label === 'Receita') {
                                  setRevenueVsExpenseView('revenue_breakdown');
                                }
                              }}
                              cursor={
                                revenueVsExpenseView === 'summary' ? 'pointer' : 'default'
                              }
                            >
                              <LabelList
                                dataKey="realized"
                                position="right"
                                offset={8}
                                formatter={(value: number) => formatCurrency(Number(value || 0))}
                                style={{ fill: 'hsl(var(--muted-foreground))', fontSize: 14, fontWeight: 600 }}
                              />
                              {revenueVsExpenseChartData.map((entry, index: number) => (
                                <Cell key={index} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                </FeatureGuard>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <FeatureGuard feature="relatorios-grafico-meios-pagamento" fallback={<FeatureLockedCard title="Meios de Pagamento" />}>
                <Card>
                  <CardHeader>
                    <CardTitle style={{ fontSize: '14px' }}>Meios de Pagamento</CardTitle>
                    <CardDescription>
                      Distribuição real das despesas pagas com meio de pagamento informado
                    </CardDescription>
                  </CardHeader>
                <CardContent>
                  {(() => {
                    if (isExpenseLoading)
                      return (
                        <div className="flex h-[330px] items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      );
                    if (paymentMethodBarChartData.length > 0)
                      return (
                        <div className="flex h-[330px] items-center justify-center gap-4">
                          <div className="h-full w-[240px] shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart margin={{ top: 8, right: 0, bottom: 8, left: 0 }}>
                                <Pie
                                  data={paymentMethodBarChartData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={100}
                                  paddingAngle={0}
                                  dataKey="value"
                                  nameKey="name"
                                  labelLine={false}
                                >
                                  {paymentMethodBarChartData.map((entry, index: number) => (
                                    <Cell
                                      key={index}
                                      fill={getPaymentMethodColor(entry.name, index)}
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  content={({ active, payload }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                    const item = payload[0]?.payload;
                                if (!item) return null;
                                return (
                                      <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                        <p className="text-sm font-medium">{item.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {formatCurrency(Number(item.value || 0))}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                          {formatPercentage(Number(item.percentage || 0))}
                                        </p>
                                      </div>
                                );
                                  }}
                                />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          </div>
                          <ul className="flex shrink-0 flex-col gap-2 pr-2 text-sm">
                            {paymentMethodBarChartData.map((item, index: number) => (
                              <li key={index} className="flex items-center gap-2 whitespace-nowrap">
                                <span
                                  className="inline-block h-2.5 w-2.5 rounded-full"
                                  style={{ background: getPaymentMethodColor(item.name, index) }}
                                />
                                <span className="text-foreground">
                                  {item.name} · {Math.round(Number(item.percentage || 0))}%
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    if (paymentMethodRealizedTotal > 0)
                      return (
                        <div className="flex h-[330px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Existem despesas pagas no período, mas nenhuma com meio de pagamento informado.
                        </div>
                      );
                    return (
                        <div className="flex h-[330px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Nenhuma despesa realizada com meio de pagamento informado neste período.
                        </div>
                      );
                  })()}
                </CardContent>
              </Card>
                </FeatureGuard>

              <FeatureGuard feature="relatorios-grafico-analise-agente" fallback={<FeatureLockedCard title="Análise por Agente" />}>
              <Card>
                <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <CardTitle style={{ fontSize: '14px' }}>Análise por Agente</CardTitle>
                    <CardDescription>
                      {agentAnalysisMode === 'revenue'
                        ? 'Recebimentos realizados por agente'
                        : 'Despesas realizadas por agente'}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={agentAnalysisMode === 'revenue' ? 'default' : 'outline'}
                      onClick={() => setAgentAnalysisMode('revenue')}
                    >
                      Receita
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={agentAnalysisMode === 'expense' ? 'default' : 'outline'}
                      onClick={() => setAgentAnalysisMode('expense')}
                    >
                      Despesa
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    if (agentAnalysisLoading) return (
                        <div className="flex h-[330px] items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    );
                    if (agentAnalysisChartData.length > 0) return (
                        <div className="h-[330px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={agentAnalysisChartData}
                              layout="vertical"
                              margin={{ top: 8, right: 96, left: 16, bottom: 8 }}
                            >
                              <XAxis type="number" tickFormatter={(value: number) => formatCurrency(value)} />
                              <YAxis type="category" dataKey="name" width={120} />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload || payload.length === 0) return null;
                                  const item = payload[0]?.payload;
                                  if (!item) return null;
                                  return (
                                    <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                      <p className="text-sm font-medium">{item.name}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {formatCurrency(Number(item.value || 0))}
                                      </p>
                                      <p className="text-sm text-muted-foreground">
                                        {formatPercentage(Number(item.percentage || 0))}
                                      </p>
                                    </div>
                                  );
                                }}
                              />
                              <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={48}>
                                <LabelList
                                  dataKey="value"
                                  position="right"
                                  offset={8}
                                  formatter={(value: number) => formatCurrency(Number(value || 0))}
                                  style={{ fill: 'hsl(var(--muted-foreground))', fontSize: 14, fontWeight: 600 }}
                                />
                                {agentAnalysisChartData.map((_, index: number) => (
                                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                    );
                    return (
                        <div className="flex h-[330px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          {agentAnalysisEmptyMessage}
                        </div>
                    );
                  })()}
                </CardContent>
              </Card>
              </FeatureGuard>
              </div>
            </>
          )}
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow" className="space-y-6 mt-6">
          {isCashFlowLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : cashFlowReport && (
            <>
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-base">FLUXO DE CAIXA</CardTitle>
                    <CardDescription>{periodLabel}</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={cashFlowMode === 'planned' ? 'default' : 'outline'}
                      onClick={() => setCashFlowMode('planned')}
                    >
                      Planejado
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={cashFlowMode === 'realized' ? 'default' : 'outline'}
                      onClick={() => setCashFlowMode('realized')}
                    >
                      Realizado
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <KPICard
                  title="Saldo Inicial"
                  value={cashFlowReport.opening_balance}
                  format="currency"
                  icon={Wallet}
                  variant={cashFlowReport.opening_balance >= 0 ? 'default' : 'danger'}
                />
                <KPICard
                  title="Entradas"
                  value={(cashFlowReport.totals?.revenues ?? 0)}
                  format="currency"
                  icon={ArrowUpRight}
                  variant="success"
                />
                <KPICard
                  title="Saídas"
                  value={(cashFlowReport.totals?.expenses ?? 0)}
                  format="currency"
                  icon={ArrowDownRight}
                  variant="danger"
                />
                <KPICard
                  title="Saldo Final"
                  value={(cashFlowReport.totals?.ending_balance ?? 0)}
                  format="currency"
                  icon={DollarSign}
                  variant={(cashFlowReport.totals?.ending_balance ?? 0) >= 0 ? 'success' : 'danger'}
                />
              </div>

              <FeatureGuard feature="relatorios-grafico-fluxo-de-caixa" fallback={<FeatureLockedCard title="Lançamentos do Fluxo" />}>
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <CardTitle className="text-base">Lançamentos do Fluxo</CardTitle>
                    <CardDescription>
                      Itens sem data definida aparecem ao final da lista.
                    </CardDescription>
                  </div>
                  {/* Exportação de dados - requer exportacao-de-dados */}
                  <FeatureGuard
                    feature="exportacao-de-dados"
                    fallback={<FeatureLockButton featureName="Exportação de Dados" label="Exportar PDF" size="sm" />}
                  >
                    <Button type="button" variant="outline" size="sm" onClick={handleExportCashFlowPdf}>
                      <Download className="mr-2 h-4 w-4" />
                      Exportar PDF
                    </Button>
                  </FeatureGuard>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="border-r border-border/60">Data</TableHead>
                        <TableHead className="border-r border-border/60">Descrição</TableHead>
                        <TableHead className="border-r border-border/60">Tipo</TableHead>
                        <TableHead className="border-r border-border/60 text-right">Receitas</TableHead>
                        <TableHead className="border-r border-border/60 text-right">Despesas</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cashFlowMode === 'realized'
                        ? (cashFlowReport.items ?? []).filter((item) => item.source_type !== 'opening_balance')
                        : (cashFlowReport.items ?? [])
                      ).map((item) => (
                        <TableRow
                          key={item.id}
                          className={item.source_type === 'opening_balance' ? 'bg-muted/30' : undefined}
                        >
                          <TableCell className="whitespace-nowrap border-r border-border/60 font-medium">
                            {formatCashFlowDate(item.date)}
                          </TableCell>
                          <TableCell className="border-r border-border/60">{item.description}</TableCell>
                          <TableCell className="border-r border-border/60">
                            <Badge variant={item.source_type === 'revenue' || item.source_type === 'opening_balance' ? 'default' : 'secondary'}>
                              {item.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="border-r border-border/60 text-right font-medium text-emerald-600">
                            {item.revenues !== 0 ? formatCurrency(item.revenues) : '-'}
                          </TableCell>
                          <TableCell className="border-r border-border/60 text-right font-medium text-rose-600">
                            {item.expenses !== 0 ? formatCurrency(item.expenses) : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${item.balance >= 0 ? 'text-foreground' : 'text-rose-600'}`}>
                            {formatCurrency(item.balance)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3} className="border-r border-border/60 font-semibold">
                          Totais
                        </TableCell>
                        <TableCell className="border-r border-border/60 text-right">{formatCurrency((cashFlowReport.totals?.revenues ?? 0))}</TableCell>
                        <TableCell className="border-r border-border/60 text-right">{formatCurrency((cashFlowReport.totals?.expenses ?? 0))}</TableCell>
                        <TableCell className={`text-right ${(cashFlowReport.totals?.ending_balance ?? 0) >= 0 ? '' : 'text-rose-600'}`}>
                          {formatCurrency((cashFlowReport.totals?.ending_balance ?? 0))}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
              </FeatureGuard>

              {cashFlowMode === 'planned' && (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm leading-6 text-muted-foreground">
                      <span className="font-medium text-foreground">*</span>
                      {' '}
                      Os valores demonstrados para as Despesas do Dia a dia, se referem aos valores
                      totais planejados para cada Período Orçamentário. Para visualizar os valores
                      efetivamente realizados, acesse o Fluxo de Caixa na opção "Realizado".
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
        
        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6 mt-6">
          {isTrendLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : trendReport && (
            <>
              {/* Trend Controls */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Períodos:</span>
                  <Select value={String(periodsBack)} onValueChange={(v) => setPeriodsBack(Number(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 meses</SelectItem>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="12">12 meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Badge variant={trendReport.growth_percentage >= 0 ? 'default' : 'destructive'}>
                  {trendReport.growth_percentage >= 0 ? '+' : ''}{trendReport.growth_percentage}% crescimento
                </Badge>
              </div>
              
              {/* Averages */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Média Receitas</div>
                    <CurrencyDisplay value={(trendReport.averages?.revenue ?? 0)} className="text-xl font-bold" />
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Média Despesas</div>
                    <CurrencyDisplay value={(trendReport.averages?.expense ?? 0)} className="text-xl font-bold" />
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground">Média Saldo</div>
                    <CurrencyDisplay value={(trendReport.averages?.balance ?? 0)} className="text-xl font-bold" colorize />
                  </CardContent>
                </Card>
              </div>
              
              {/* Planned vs Realized Timeline */}
              <FeatureGuard feature="relatorios-grafico-planejado-vs-realizado" fallback={<FeatureLockedCard title="Planejado vs Realizado" />}>
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle style={{ fontSize: '14px' }}>Planejado vs Realizado</CardTitle>
                      <CardDescription>
                        Comparativo entre valores planejados e realizados ao longo dos períodos
                      </CardDescription>
                    </div>
                    <Select
                      value={plannedVsRealizedMode}
                      onValueChange={(v) => setPlannedVsRealizedMode(v as 'revenue' | 'expense')}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="revenue">Receitas</SelectItem>
                        <SelectItem value="expense">Despesas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <XAxis dataKey="po_label" {...getPoAxisProps(trendChartData.length, isCompactScreen)} />
                        <YAxis width={96} tickFormatter={formatCurrency} />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey={plannedVsRealizedMode === 'revenue' ? 'planned_revenue' : 'planned_expense'}
                          name="Planejado"
                          stroke="hsl(220, 14%, 50%)"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey={plannedVsRealizedMode === 'revenue' ? 'realized_revenue' : 'realized_expense'}
                          name="Realizado"
                          stroke={plannedVsRealizedMode === 'revenue' ? 'hsl(152, 69%, 40%)' : 'hsl(0, 72%, 51%)'}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              </FeatureGuard>

              {/* Trend Chart */}
              <FeatureGuard feature="relatorios-grafico-evolucao-tempo" fallback={<FeatureLockedCard title="Evolução ao Longo do Tempo" />}>
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '14px' }}>Evolução ao Longo do Tempo</CardTitle>
                  <CardDescription>Comparativo de receitas, despesas e saldo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <XAxis dataKey="po_label" {...getPoAxisProps(trendChartData.length, isCompactScreen)} />
                        <YAxis width={96} tickFormatter={formatCurrency} />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="realized_revenue"
                          name="Receitas"
                          stroke="hsl(152, 69%, 40%)"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="realized_expense"
                          name="Despesas"
                          stroke="hsl(0, 72%, 51%)"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="realized_balance"
                          name="Saldo"
                          stroke="hsl(174, 62%, 38%)"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              </FeatureGuard>
            </>
          )}
        </TabsContent>
        
        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-6 mt-6">
          <Tabs value={expenseSectionTab} onValueChange={setExpenseSectionTab} className="space-y-6">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-2">
              <TabsTrigger value="expense">Despesas</TabsTrigger>
              <TabsTrigger value="future-expenses">Despesas Futuras</TabsTrigger>
            </TabsList>

            <TabsContent value="expense" className="space-y-6 mt-0">
              {isExpenseLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : expenseReport ? (
                <>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <KPICard
                      title="Despesas Mensais Vencidas"
                      value={monthlyOverdueCount}
                      format="number"
                      icon={AlertTriangle}
                      variant={monthlyOverdueCount > 0 ? 'danger' : 'success'}
                    />
                    <KPICard
                      title="Despesas Mensais Pendentes"
                      value={monthlyPendingCount}
                      format="number"
                      icon={Clock}
                      variant={monthlyPendingCount > 3 ? 'warning' : 'default'}
                    />
                    <KPICard
                      title="Dia a Dia: Saldo Restante"
                      value={dailyRemainingBudget}
                      format="currency"
                      icon={DollarSign}
                      variant={dailyRemainingBudget >= 0 ? 'success' : 'danger'}
                      subtitle={periodLabel}
                    />
                    <KPICard
                      title="Dia a Dia: Quanto você ainda pode gastar"
                      value={daysRemaining > 0 ? dailyCapacity : 0}
                      format="currency"
                      icon={Coffee}
                      variant={dailyCapacity >= 0 ? 'default' : 'danger'}
                      subtitle={daysRemaining > 0 ? `Sugestão por dia (${daysRemaining} dias)` : 'Período encerrado'}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <KPICard
                      title="Planejado"
                      value={(expenseReport.totals?.planned ?? 0)}
                      format="currency"
                      icon={Calendar}
                      variant="default"
                    />
                    <KPICard
                      title="Realizado"
                      value={(expenseReport.totals?.realized ?? 0)}
                      format="currency"
                      icon={ArrowDownRight}
                      variant="danger"
                    />
                    <KPICard
                      title="Economia"
                      value={(expenseReport.totals?.variance ?? 0)}
                      format="currency"
                      icon={TrendingDown}
                      variant={(expenseReport.totals?.variance ?? 0) >= 0 ? 'success' : 'danger'}
                    />
                  </div>

                  <FeatureGuard feature="relatorios-grafico-linha-tempo-despesas" fallback={<FeatureLockedCard title="Linha do Tempo de Despesas por Categoria" />}>
                  <Card>
                    <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-1">
                        <CardTitle style={{ fontSize: '14px' }}>Linha do Tempo de Despesas por Categoria</CardTitle>
                        <CardDescription>
                          Categorias com maior gasto realizado nos últimos 3, 6 ou 12 meses
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Período</span>
                          <Select
                            value={String(expenseTimelinePeriodsBack)}
                            onValueChange={(value) => setExpenseTimelinePeriodsBack(Number(value))}
                          >
                            <SelectTrigger className="w-[108px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3">3 meses</SelectItem>
                              <SelectItem value="6">6 meses</SelectItem>
                              <SelectItem value="12">12 meses</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Métricas</span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" variant="outline" size="sm" className="gap-2">
                                Categorias
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72">
                              <DropdownMenuItem onClick={showAllExpenseTimelineCategories}>
                                Mostrar Todas
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {expenseTimelineAvailableCategories.map((category) => (
                                <DropdownMenuCheckboxItem
                                  key={category.category}
                                  checked={selectedExpenseTimelineCategories.includes(category.category)}
                                  onCheckedChange={(checked) =>
                                    toggleExpenseTimelineCategory(category.category, checked)
                                  }
                                  onSelect={(event) => event.preventDefault()}
                                >
                                  <div className="flex w-full items-center justify-between gap-3">
                                    <span>{translateCategory(category.category)}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {formatCurrency(Number(category.total_realized || 0))}
                                    </span>
                                  </div>
                                </DropdownMenuCheckboxItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        if (isExpenseCategoryTimelineLoading) return (
                            <div className="flex h-[360px] items-center justify-center">
                              <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                        );
                        if (isExpenseCategoryTimelineError) return (
                            <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                              Não foi possível carregar o gráfico de categorias no momento.
                            </div>
                        );
                        if (expenseTimelineAvailableCategories.length > 0) return (
                            selectedExpenseTimelineSeries.length > 0 ? (
                              <div className="h-[360px]">
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={expenseTimelineChartData} margin={{ top: 8, right: 12, left: 16, bottom: 24 }}>
                                    <XAxis
                                      dataKey="po_label"
                                      tickMargin={12}
                                      {...getPoAxisProps(expenseTimelineChartData.length, isCompactScreen)}
                                    />
                                    <YAxis tickFormatter={(value: number) => formatCurrency(Number(value || 0))} width={120} tickMargin={8} />
                                    <Tooltip
                                      content={({ active, payload, label }) => {
                                        if (!active || !payload || payload.length === 0) {
                                          return null;
                                        }

                                        const item = payload[0]?.payload as { reference_label?: string } | undefined;
                                        const sortedPayload = [...payload].sort(
                                          (left, right) =>
                                            Number(right?.value || 0) - Number(left?.value || 0),
                                        );
                                        const title = item?.reference_label
                                          ? `${String(label || '')} . ${item.reference_label}`
                                          : String(label || '');

                                        return (
                                          <div className="min-w-[190px] rounded-md border border-border/60 bg-background px-3 py-3 shadow-md">
                                            <div className="mb-2 rounded-sm bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                              {title}
                                            </div>
                                            <div className="space-y-1.5">
                                              {sortedPayload.map((entry) => (
                                                <div key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
                                                  {translateCategory(String(entry.name || ''))}: {formatCurrency(Number(entry.value || 0))}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      }}
                                    />
                                    <Legend formatter={(value) => translateCategory(String(value || ''))} />
                                    {selectedExpenseTimelineSeries.map((category) => (
                                      <Line
                                        key={category.category}
                                        type="monotone"
                                        dataKey={category.category}
                                        name={category.category}
                                        stroke={getExpenseTimelineColor(category.category)}
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                      />
                                    ))}
                                  </LineChart>
                                </ResponsiveContainer>
                              </div>
                            ) : (
                              <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                                Selecione ao menos uma categoria no botão Categorias para visualizar o gráfico.
                              </div>
                            )
                        );
                        return (
                            <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                              Nenhuma despesa realizada por categoria foi encontrada no período selecionado.
                            </div>
                        );
                      })()}
                      <p className="mt-4 text-xs leading-5 text-muted-foreground">
                        Observação: o gráfico exibe as 5 categorias com maior valor de despesa. Para visualizar as
                        demais, clique no campo Métricas e selecione a categoria desejada ou a opção "Mostrar Todas".
                      </p>
                    </CardContent>
                  </Card>
                  </FeatureGuard>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <FeatureGuard feature="relatorios-grafico-despesas-por-categoria" fallback={<FeatureLockedCard title="Despesas por Categoria" />}>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Despesas por Categoria</CardTitle>
                        <CardDescription>Distribuição das despesas realizadas</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          {(() => {
                            const realizedCategories = (expenseReport.by_category ?? []).filter(
                              (cat) => Number(cat.realized) > 0
                            );
                            return realizedCategories.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <RechartsPieChart>
                                  <Pie
                                    data={realizedCategories.map((cat, i: number) => ({
                                      category: translateCategory(cat.category),
                                      name: translateCategory(cat.category),
                                      value: Number(cat.realized) || 0,
                                      percentage: Number(cat.percentage) || 0,
                                      color: COLORS[i % COLORS.length],
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={115}
                                    paddingAngle={0}
                                    dataKey="value"
                                    nameKey="category"
                                    labelLine={false}
                                  >
                                    {realizedCategories.map((_, i: number) => (
                                      <Cell
                                        key={`cell-${i}`}
                                        fill={COLORS[i % COLORS.length]}
                                        stroke="hsl(var(--background))"
                                        strokeWidth={0}
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload || payload.length === 0) return null;
                                      const item = payload[0]?.payload;
                                      if (!item) return null;
                                      return (
                                        <div className="rounded-md border bg-background px-3 py-2 shadow-sm">
                                          <p className="text-sm font-medium" style={{ color: item.color }}>
                                            {item.name}
                                          </p>
                                          <p className="text-sm text-muted-foreground">
                                            {formatCurrency(Number(item.value || 0))}
                                          </p>
                                          <p className="text-sm text-muted-foreground">
                                            {Number(item.percentage || 0).toFixed(1)}%
                                          </p>
                                        </div>
                                      );
                                    }}
                                  />
                                  <Legend
                                    layout="horizontal"
                                    align="center"
                                    verticalAlign="bottom"
                                    wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                                  />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                                <p>Nenhuma despesa realizada</p>
                                <p className="mt-1 text-xs">Categorias aparecerão após realizar despesas</p>
                              </div>
                            );
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                    </FeatureGuard>
                  </div>
                </>
              ) : null}
            </TabsContent>

            <TabsContent value="future-expenses" className="space-y-6 mt-0">
              {isFutureExpenseSummaryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <FeatureGuard feature="relatorios-grafico-sintese-despesas-futuras" fallback={<FeatureLockedCard title="Síntese de Despesas Futuras" />}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Síntese de Despesas Futuras</CardTitle>
                    <CardDescription>
                      {periodLabel || 'Período Orçamentário atual'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {futureExpenseSummaryReport && (futureExpenseSummaryReport.rows ?? []).length > 0 ? (
                      <div className="rounded-md border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30">
                              <TableHead className="border-r border-border/60">Quando</TableHead>
                              <TableHead className="border-r border-border/60">Despesas</TableHead>
                              <TableHead className="border-r border-border/60 text-right">Planejado</TableHead>
                              <TableHead className="border-r border-border/60 text-right">Realizado</TableHead>
                              <TableHead className="border-r border-border/60 text-right">Acumulado</TableHead>
                              <TableHead className="text-right">Evolução</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(futureExpenseSummaryReport.rows ?? []).map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="border-r border-border/60 font-medium">
                                  {formatCashFlowDate(item.when)}
                                </TableCell>
                                <TableCell className="border-r border-border/60">
                                  {item.expense}
                                </TableCell>
                                <TableCell className="border-r border-border/60 text-right">
                                  {formatCurrency(item.planned)}
                                </TableCell>
                                <TableCell className="border-r border-border/60 text-right">
                                  {formatCurrency(item.realized)}
                                </TableCell>
                                <TableCell className="border-r border-border/60 text-right">
                                  {formatCurrency(item.accumulated)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatPercentage(item.evolution)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          <TableFooter>
                            <TableRow className="bg-muted/30 font-semibold">
                              <TableCell colSpan={2} className="border-r border-border/60">
                                Total
                              </TableCell>
                              <TableCell className="border-r border-border/60 text-right">
                                {formatCurrency((futureExpenseSummaryReport.totals?.planned ?? 0))}
                              </TableCell>
                              <TableCell className="border-r border-border/60 text-right">
                                {formatCurrency((futureExpenseSummaryReport.totals?.realized ?? 0))}
                              </TableCell>
                              <TableCell className="border-r border-border/60 text-right">
                                {formatCurrency((futureExpenseSummaryReport.totals?.accumulated ?? 0))}
                              </TableCell>
                              <TableCell className="text-right">-</TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
                        Nenhuma despesa futura encontrada neste período orçamentário.
                      </div>
                    )}
                  </CardContent>
                </Card>
                </FeatureGuard>
              )}

              {(() => {
                const selectedExpense =
                  futureExpenseProjectionReport && futureExpenseView === 'unit'
                    ? (futureExpenseProjectionReport.expenses ?? []).find((e) => e.id === futureExpenseSelectedId)
                      ?? futureExpenseProjectionReport.expenses[0]
                    : null;
                const rawPoints =
                  futureExpenseView === 'unit' && selectedExpense
                    ? selectedExpense.points
                    : futureExpenseProjectionReport?.points ?? [];
                const targetLineValue =
                  futureExpenseView === 'unit'
                    ? selectedExpense?.target_amount ?? 0
                    : futureExpenseProjectionReport?.summary.total_target ?? 0;
                let chartPoints = rawPoints;
                if (rawPoints.length > 0) {
                  // Stop at the last PO before the goal's target_date — after that
                  // there are no contributions left (values are 0).
                  const firstZeroIdx = rawPoints.findIndex(
                    (p, idx) => idx > 0 && p.planned === 0 && p.required === 0 && p.projected === 0,
                  );
                  if (firstZeroIdx > 0) {
                    chartPoints = rawPoints.slice(0, firstZeroIdx);
                  }
                }
                const summary = futureExpenseProjectionReport?.summary;
                const plannedMonthly =
                  futureExpenseView === 'unit' && selectedExpense
                    ? selectedExpense.planned_monthly
                    : summary?.planned_monthly ?? 0;
                const accumulatedNow =
                  futureExpenseView === 'unit' && selectedExpense
                    ? selectedExpense.accumulated
                    : summary?.current_accumulated ?? 0;
                const requiredMonthly =
                  futureExpenseView === 'unit' && selectedExpense
                    ? selectedExpense.required_monthly
                    : summary?.required_monthly ?? 0;

                return (
                  <FeatureGuard feature="relatorios-grafico-projecao-despesas-futuras" fallback={<FeatureLockedCard title="Projeção de Despesas Futuras" />}>
                  <Card>
                    <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">Projeção de Despesas Futuras</CardTitle>
                        <CardDescription>
                          {futureExpenseView === 'unit'
                            ? 'Projeção individual da despesa selecionada'
                            : 'Acumulado esperado se você mantiver o ritmo atual de contribuição'}
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          value={futureExpenseView}
                          onValueChange={(value) => setFutureExpenseView(value as 'accumulated' | 'unit')}
                        >
                          <SelectTrigger className="w-[148px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accumulated">Acumulado</SelectItem>
                            <SelectItem value="unit">Unitário</SelectItem>
                          </SelectContent>
                        </Select>
                        {futureExpenseView === 'unit' && futureExpenseProjectionReport && (futureExpenseProjectionReport.expenses ?? []).length > 0 && (
                          <Select
                            value={selectedExpense?.id ?? ''}
                            onValueChange={(value) => setFutureExpenseSelectedId(value)}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Selecione a despesa" />
                            </SelectTrigger>
                            <SelectContent>
                              {(futureExpenseProjectionReport.expenses ?? []).map((exp) => (
                                <SelectItem key={exp.id} value={exp.id}>
                                  {exp.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Select
                          value={String(futureExpenseMonthsAhead)}
                          onValueChange={(value) => setFutureExpenseMonthsAhead(Number(value) as 6 | 12 | 18 | 24)}
                        >
                          <SelectTrigger className="w-[148px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                            <SelectItem value="18">18 meses</SelectItem>
                            <SelectItem value="24">24 meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isFutureExpenseProjectionLoading ? (
                        <div className="flex h-[360px] items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                      ) : isFutureExpenseProjectionError ? (
                        <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Não foi possível carregar o gráfico de projeção no momento.
                        </div>
                      ) : futureExpenseProjectionReport && chartPoints.length > 0 ? (
                        <div className="space-y-4">
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Acumulado hoje</div>
                              <div className="mt-1 text-sm font-semibold">{formatCurrency(accumulatedNow)}</div>
                            </div>
                            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Planejado/mês</div>
                              <div className="mt-1 text-sm font-semibold">{formatCurrency(plannedMonthly)}</div>
                            </div>
                            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Necessário/mês</div>
                              <div className="mt-1 text-sm font-semibold text-amber-600 dark:text-amber-400">
                                {formatCurrency(requiredMonthly)}
                              </div>
                            </div>
                            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Meta</div>
                              <div className="mt-1 text-sm font-semibold text-amber-600 dark:text-amber-400">
                                {formatCurrency(targetLineValue)}
                              </div>
                            </div>
                          </div>
                          <div className="h-[360px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={chartPoints}
                                margin={{ top: 8, right: 12, left: 8, bottom: 24 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                  dataKey="period_label"
                                  tickMargin={12}
                                  {...getPoAxisProps(chartPoints.length, isCompactScreen)}
                                />
                                <YAxis
                                  tickFormatter={(value: number) =>
                                    new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(Number(value || 0))
                                  }
                                  width={72}
                                  tickMargin={8}
                                  domain={[0, (dataMax: number) => Math.max(dataMax, targetLineValue) * 1.05]}
                                />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (!active || !payload || payload.length === 0) {
                                      return null;
                                    }
                                    const item = payload[0]?.payload as {
                                      reference_label?: string;
                                      period_label?: string;
                                      planned_period?: number;
                                      realized_period?: number;
                                      projected_period?: number;
                                    } | undefined;
                                    const heading = item?.period_label ?? String(label || '');
                                    const title = item?.reference_label
                                      ? `${heading} · ${item.reference_label}`
                                      : heading;
                                    // Cada linha do tooltip mostra o aporte do PO
                                    // (mensal), enquanto a curva segue cumulativa.
                                    const periodValueFor = (dataKey: string): number => {
                                      if (dataKey === 'planned') return Number(item?.planned_period ?? 0);
                                      if (dataKey === 'projected') return Number(item?.realized_period ?? 0);
                                      if (dataKey === 'required') return Number(item?.projected_period ?? 0);
                                      return 0;
                                    };
                                    return (
                                      <div className="min-w-[200px] rounded-md border border-border/60 bg-background px-3 py-3 shadow-md">
                                        <div className="mb-2 rounded-sm bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                          {title}
                                        </div>
                                        <div className="space-y-1.5">
                                          {payload.map((entry) => (
                                            <div key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
                                              {entry.name}: {formatCurrency(periodValueFor(String(entry.dataKey)))}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }}
                                />
                                <Legend wrapperStyle={{ paddingTop: 24 }} />
                                {targetLineValue > 0 && (
                                  <ReferenceLine
                                    y={targetLineValue}
                                    stroke="hsl(152, 69%, 25%)"
                                    strokeWidth={2}
                                    label={{
                                      value: `Meta · ${formatCurrency(targetLineValue)}`,
                                      position: 'insideTopRight',
                                      fill: 'hsl(152, 69%, 25%)',
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                                <Line
                                  type="monotone"
                                  dataKey="planned"
                                  name="Planejado"
                                  stroke="hsl(220, 14%, 50%)"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  activeDot={{ r: 5 }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="projected"
                                  name="Realizado"
                                  stroke="hsl(174, 62%, 38%)"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  activeDot={{ r: 5 }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="required"
                                  name="Projetado"
                                  stroke="hsl(38, 92%, 50%)"
                                  strokeWidth={2}
                                  dot={{ r: 3 }}
                                  activeDot={{ r: 5 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Cadastre uma despesa futura para visualizar a projeção.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  </FeatureGuard>
                );
              })()}
            </TabsContent>
          </Tabs>
        </TabsContent>
        
        {/* Revenues Tab */}
        <TabsContent value="revenues" className="space-y-6 mt-6">
          {isRevenueLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : revenueReport && (
            <>
              {/* Indicadores operacionais (antes exibidos na página de Receitas) */}
              <div className="grid gap-4 md:grid-cols-2">
                <KPICard
                  title="Receitas Pendentes"
                  value={revenuePendingCount}
                  format="number"
                  icon={Clock}
                  variant={revenuePendingCount > 0 ? 'warning' : 'default'}
                />
                <KPICard
                  title="Receitas Realizadas"
                  value={revenueRealizedCount}
                  format="number"
                  icon={CheckCircle2}
                  variant="success"
                />
              </div>

              {/* Revenue Totals */}
              <div className="grid gap-4 md:grid-cols-3">
                <KPICard
                  title="Planejado"
                  value={(revenueReport.totals?.planned ?? 0)}
                  format="currency"
                  icon={Calendar}
                  variant="default"
                />
                <KPICard
                  title="Realizado"
                  value={(revenueReport.totals?.realized ?? 0)}
                  format="currency"
                  icon={ArrowUpRight}
                  variant="success"
                />
                <KPICard
                  title="Variação"
                  value={(revenueReport.totals?.variance ?? 0)}
                  format="currency"
                  icon={TrendingUp}
                  variant={(revenueReport.totals?.variance ?? 0) >= 0 ? 'success' : 'danger'}
                />
              </div>
              
              {/* Revenue Type Timeline */}
              <FeatureGuard feature="relatorios-grafico-linha-tempo-receitas" fallback={<FeatureLockedCard title="Linha do Tempo de Receitas por Tipo" />}>
              <Card>
                <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="space-y-1">
                    <CardTitle style={{ fontSize: '14px' }}>Linha do Tempo de Receitas por Tipo</CardTitle>
                    <CardDescription>
                      Tipos de receita com maior valor realizado nos últimos 3, 6 ou 12 meses
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Período</span>
                      <Select
                        value={String(revenueTimelinePeriodsBack)}
                        onValueChange={(value) => setRevenueTimelinePeriodsBack(Number(value))}
                      >
                        <SelectTrigger className="w-[108px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 meses</SelectItem>
                          <SelectItem value="6">6 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Métricas</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="outline" size="sm" className="gap-2">
                            Tipos
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-72">
                          <DropdownMenuItem onClick={showAllRevenueTimelineTypes}>
                            Mostrar Todos
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {revenueTimelineAvailableTypes.map((type) => (
                            <DropdownMenuCheckboxItem
                              key={type.type}
                              checked={selectedRevenueTimelineTypes.includes(type.type)}
                              onCheckedChange={(checked) =>
                                toggleRevenueTimelineType(type.type, checked)
                              }
                              onSelect={(event) => event.preventDefault()}
                            >
                              <div className="flex w-full items-center justify-between gap-3">
                                <span>{translateRevenueType(type.type)}</span>
                                <span className="text-xs text-muted-foreground">
                                  {formatCurrency(Number(type.total_realized || 0))}
                                </span>
                              </div>
                            </DropdownMenuCheckboxItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    if (isRevenueTypeTimelineLoading) return (
                        <div className="flex h-[360px] items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    );
                    if (isRevenueTypeTimelineError) return (
                        <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Não foi possível carregar o gráfico de tipos no momento.
                        </div>
                    );
                    if (revenueTimelineAvailableTypes.length > 0) return (
                        selectedRevenueTimelineSeries.length > 0 ? (
                          <div className="h-[360px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={revenueTimelineChartData} margin={{ top: 8, right: 12, left: 16, bottom: 24 }}>
                                <XAxis
                                  dataKey="po_label"
                                  tickMargin={12}
                                  {...getPoAxisProps(revenueTimelineChartData.length, isCompactScreen)}
                                />
                                <YAxis tickFormatter={(value: number) => formatCurrency(Number(value || 0))} width={120} tickMargin={8} />
                                <Tooltip
                                  content={({ active, payload, label }) => {
                                    if (!active || !payload || payload.length === 0) {
                                      return null;
                                    }

                                    const item = payload[0]?.payload as { reference_label?: string } | undefined;
                                    const orderedPayload = [...payload].sort(
                                      (left, right) =>
                                        getRevenueTimelineOrderIndex(String(left?.dataKey || '')) -
                                        getRevenueTimelineOrderIndex(String(right?.dataKey || '')),
                                    );
                                    const total = payload.reduce(
                                      (sum: number, entry) => sum + Number(entry?.value || 0),
                                      0,
                                    );
                                    const title = item?.reference_label
                                      ? `${String(label || '')} . ${item.reference_label}`
                                      : String(label || '');

                                    return (
                                      <div className="min-w-[190px] rounded-md border border-border/60 bg-background px-3 py-3 shadow-md">
                                        <div className="mb-2 rounded-sm bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">
                                          {title}
                                        </div>
                                        <div className="mb-2 flex items-center justify-between gap-3 text-sm font-semibold">
                                          <span>Total</span>
                                          <span>{formatCurrency(total)}</span>
                                        </div>
                                        <div className="space-y-1.5 border-t border-border/60 pt-2">
                                          {orderedPayload.map((entry) => (
                                            <div
                                              key={entry.dataKey}
                                              className="flex items-center justify-between gap-3 text-sm text-foreground"
                                            >
                                              <span className="flex items-center gap-1.5">
                                                <span
                                                  aria-hidden="true"
                                                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                                                  style={{ backgroundColor: entry.color }}
                                                />
                                                {translateRevenueType(String(entry.name || ''))}
                                              </span>
                                              <span>{formatCurrency(Number(entry.value || 0))}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }}
                                />
                                <Legend formatter={renderRevenueTimelineLegendLabel} />
                                {selectedRevenueTimelineSeries.map((type) => (
                                  <Bar
                                    key={type.type}
                                    dataKey={type.type}
                                    name={type.type}
                                    stackId="revenue-timeline"
                                    fill={getRevenueTimelineColor(type.type)}
                                    maxBarSize={56}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                            Selecione ao menos um tipo no botão Tipos para visualizar o gráfico.
                          </div>
                        )
                    );
                    return (
                        <div className="flex h-[360px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                          Nenhuma receita realizada por tipo foi encontrada no período selecionado.
                        </div>
                    );
                  })()}
                </CardContent>
              </Card>
              </FeatureGuard>

              {/* Revenue by Type */}
              <div className="grid gap-6 lg:grid-cols-2">
                <FeatureGuard feature="relatorios-grafico-receitas-por-tipo" fallback={<FeatureLockedCard title="Receitas por Tipo" />}>
                <Card>
                  <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-base">Receitas por Tipo</CardTitle>
                      <CardDescription>
                        Distribuição por tipo de receita ({isRevenueByTypePlanned ? 'Planejado' : 'Realizado'})
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={revenueByTypeMode === 'planned' ? 'default' : 'outline'}
                        onClick={() => setRevenueByTypeMode('planned')}
                      >
                        Planejado
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={revenueByTypeMode === 'realized' ? 'default' : 'outline'}
                        onClick={() => setRevenueByTypeMode('realized')}
                      >
                        Realizado
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {revenueByTypeChartData.length > 0 ? (
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={revenueByTypeChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                            >
                              {revenueByTypeChartData.map((_, i: number) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="flex h-[300px] items-center justify-center rounded-lg border border-dashed text-center text-sm text-muted-foreground">
                        {isRevenueByTypePlanned
                          ? 'Nenhuma receita planejada por tipo neste período.'
                          : 'Nenhuma receita realizada por tipo neste período.'}
                      </div>
                    )}
                  </CardContent>
                </Card>
                </FeatureGuard>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ReportsPage;
